import React from "react";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_ROOM_WALL_COLOR_ID,
  getRoomWallColor,
  ROOM_WALL_COLORS,
  RoomWallColorId,
} from "./roomWallColors";

interface RoomWallColorPickerProps {
  value: RoomWallColorId;
  onChange: (id: RoomWallColorId) => void;
}

/**
 * Swatch grid for selecting wall fill color in the room-picker wizard.
 */
export const RoomWallColorPicker: React.FC<RoomWallColorPickerProps> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const selected = getRoomWallColor(value);

  return (
    <div>
      <div className="mb-1 text-sm font-medium text-gray-800">
        {t("roomPicker.wallColor")}
      </div>
      <p className="mb-2 text-xs text-gray-500">
        {t("roomPicker.wallColorHint")}
      </p>
      <div className="mb-2 flex items-center gap-2 text-xs text-gray-600">
        <span
          className="h-4 w-4 rounded border border-gray-300"
          style={{ backgroundColor: selected.hex }}
          aria-hidden
        />
        <span>{t(`roomPicker.wallColors.${selected.labelKey}`)}</span>
      </div>
      <div
        className="grid grid-cols-5 gap-2"
        role="listbox"
        aria-label={t("roomPicker.wallColor")}
      >
        {ROOM_WALL_COLORS.map((option) => {
          const isSelected = option.id === value;
          return (
            <button
              key={option.id}
              type="button"
              role="option"
              aria-selected={isSelected}
              title={t(`roomPicker.wallColors.${option.labelKey}`)}
              aria-label={t(`roomPicker.wallColors.${option.labelKey}`)}
              onClick={() => onChange(option.id)}
              className={`h-9 w-full rounded-md border transition-shadow ${
                isSelected
                  ? "border-black ring-2 ring-black ring-offset-1"
                  : "border-gray-300 hover:border-gray-500"
              }`}
              style={{ backgroundColor: option.hex }}
            />
          );
        })}
      </div>
    </div>
  );
};

export { DEFAULT_ROOM_WALL_COLOR_ID };
